import { formatISO } from 'date-fns';
import { z } from 'zod';

import type { Contact as ApiContact } from '@/api/generated/types.gen';
import type { Contact } from '@/api/types/contacts';

// ------------------------------------------------------------------ sub-schemas

export const emailEntrySchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email'),
  type: z.enum(['personal', 'work', 'academic']).optional(),
  verified: z.boolean().optional(),
});

export const phoneSchema = z.object({
  number: z.string().optional(),
  type: z.enum(['mobile', 'home', 'work']).optional(),
  primary: z.boolean().optional(),
});

export const socialPlatformEnum = z.enum([
  '',
  'linkedin',
  'twitter',
  'facebook',
  'instagram',
  'github',
]);
export const socialProfileSchema = z.object({
  platform: socialPlatformEnum.optional(),
  url: z.string().optional(),
  username: z.string().optional(),
});

export const addressTypeEnum = z.enum(['', 'home', 'work', 'billing', 'residence']);
export const addressSchema = z.object({
  type: addressTypeEnum.optional(),
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
  primary: z.boolean().optional(),
});

export const attachmentSchema = z.object({
  fileId: z.string().min(1),
  name: z.string().min(1),
  category: z.string().optional(),
  type: z.string().optional(),
  size: z.number().optional(),
  url: z.string().optional(),
  uploadedAt: z.string(),
  uploadedBy: z.string(),
});

export const jobApplicationSchema = z.object({
  date: z.date().optional(),
  portal: z.string().optional(),
  jobId: z.string().optional(),
  jobTitle: z.string().optional(),
  jobText: z.string().optional(),
  institutionName: z.string().optional(),
  message: z.string().optional(),
  attachments: z.array(attachmentSchema),
});

export const educationStatusEnum = z.enum(['', 'enrolled', 'graduated', 'withdrawn']);
export const educationSchema = z.object({
  institution: z.string().min(1, 'Institution is required'),
  faculty: z.string().optional(),
  field: z.string().optional(),
  degree: z.string().optional(),
  status: educationStatusEnum.optional(),
  yearOfStudy: z.number().int().positive().optional(),
  startDate: z.date().optional(),
  endDate: z.date().optional(),
  location: z.string().optional(),
});

export const certificationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  issuer: z.string().optional(),
  credentialId: z.string().optional(),
  issuedOn: z.date().optional(),
  expiresOn: z.date().optional(),
  url: z.string().optional(),
});

export const skillCategoryEnum = z.enum(['', 'clinical', 'technical', 'soft']);
export const skillLevelEnum = z.enum(['', 'beginner', 'intermediate', 'advanced', 'expert']);
export const skillSchema = z.object({
  name: z.string().min(1, 'Skill name is required'),
  category: skillCategoryEnum.optional(),
  level: skillLevelEnum.optional(),
});

export const employmentTypeEnum = z.enum(['', 'full-time', 'part-time', 'contract', 'internship']);
export const employmentSchema = z.object({
  organization: z.string().min(1, 'Organization is required'),
  role: z.string().min(1, 'Role is required'),
  department: z.string().optional(),
  employmentType: employmentTypeEnum.optional(),
  startDate: z.date().optional(),
  endDate: z.date().optional(),
  location: z.string().optional(),
  description: z.string().optional(),
});

export const achievementSchema = z.object({
  type: z.string().min(1, 'Type is required'),
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  date: z.date().optional(),
  url: z.string().optional(),
});

export const referenceRelationshipEnum = z.enum(['', 'supervisor', 'colleague', 'professor']);
export const referenceSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  relationship: referenceRelationshipEnum.optional(),
  position: z.string().optional(),
  organization: z.string().optional(),
  email: z.union([z.string().email('Invalid email'), z.literal('')]).optional(),
  phone: z.string().optional(),
});

export const serviceTypeEnum = z.enum(['', 'subscription', 'one-time', 'recurring']);
export const serviceStatusEnum = z.enum(['', 'active', 'expired', 'cancelled']);
export const serviceSchema = z.object({
  serviceName: z.string().min(1, 'Service name is required'),
  type: serviceTypeEnum.optional(),
  status: serviceStatusEnum.optional(),
  purchasedOn: z.date().optional(),
  expiresOn: z.date().optional(),
});

export const eventTypeEnum = z.enum(['', 'conference', 'webinar', 'workshop']);
export const eventRoleEnum = z.enum(['', 'attendee', 'speaker', 'organizer']);
export const eventSchema = z.object({
  eventName: z.string().min(1, 'Event name is required'),
  type: eventTypeEnum.optional(),
  role: eventRoleEnum.optional(),
  startDate: z.date().optional(),
  endDate: z.date().optional(),
  location: z.string().optional(),
});

export const interactionTypeEnum = z.enum(['', 'email', 'call', 'meeting', 'chat']);
export const interactionSchema = z.object({
  type: interactionTypeEnum.optional(),
  subject: z.string().optional(),
  summary: z.string().optional(),
  occurredAt: z.date().optional(),
  outcome: z.string().optional(),
});

export const noteCategoryEnum = z.enum(['', 'general', 'medical', 'administrative', 'search']);
export const noteSchema = z.object({
  text: z.string().min(1, 'Note text is required'),
  category: noteCategoryEnum.optional(),
  addedAt: z.string(),
  addedBy: z.string(),
});

// Options for the fallback Gender select; the form itself accepts any string so
// values curated via a contact.profile.gender counter table survive load → save round-trips.
export const genderEnum = z.enum(['male', 'female', '']);
export const parseGender = (value: string | undefined) => value ?? '';

export const emailTypeEnum = z.enum(['personal', 'work', 'academic', '']);
export const parseEmailType = (value: z.infer<typeof emailTypeEnum> | undefined | string) => {
  const result = emailTypeEnum.safeParse(value);
  return result.success ? result.data : '';
};

export const phoneTypeEnum = z.enum(['mobile', 'home', 'work', '']);
export const parsePhoneType = (value: z.infer<typeof phoneTypeEnum> | undefined | string) => {
  const result = phoneTypeEnum.safeParse(value);
  return result.success ? result.data : '';
};

export const engagementEnum = z.enum(['', 'high', 'medium', 'low']);
export const parseEngagement = (value: string | undefined) => {
  const result = engagementEnum.safeParse(value);
  return result.success ? result.data : '';
};

const isoToDate = (value: string | undefined): Date | undefined => {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d;
};

const dateToIso = (value: Date | undefined): string | undefined =>
  value ? formatISO(value) : undefined;

const safeEnum = <T extends z.ZodEnum>(schema: T, value: unknown): z.infer<T> | undefined => {
  if (value === undefined || value === null) return undefined;
  const result = schema.safeParse(value);
  return result.success ? result.data : undefined;
};

// ------------------------------------------------------------------ root schema

export const contactFormSchema = z.object({
  profile: z.object({
    titleBefore: z.string().optional(),
    firstName: z.string().optional(),
    middleName: z.string().optional(),
    lastName: z.string().optional(),
    titleAfter: z.string().optional(),
    dateOfBirth: z.date().optional(),
    gender: z.string(),
    residence: z.string().optional(),
    city: z.string().optional(),
    nationality: z.string().optional(),
    languages: z
      .array(
        z.object({
          code: z.string(),
          name: z.string(),
          proficiency: z.string().optional(),
        }),
      )
      .optional(),
    // Lenient on purpose: legacy contacts may carry non-URL photo values and a
    // strict URL check here silently blocks the whole form's submit.
    photoUrl: z.string().optional(),
    bio: z.string().optional(),
    interest: z.string().optional(),
    // Primary "orientation" fields (counter-backed); may duplicate richer
    // education/employment data, later derived by a back-fill job.
    region: z.string().optional(),
    country: z.string().optional(),
    educationInstitution: z.string().optional(),
    occupation: z.string().optional(),
    specialization: z.string().optional(),
    company: z.string().optional(),
    position: z.string().optional(),
  }),
  contact: z.object({
    primaryEmail: z.email('Invalid email address'),
    emails: z.array(
      z.object({
        email: z.email('Invalid email address'),
        type: emailTypeEnum,
      }),
    ),
    phones: z.array(
      z.object({
        number: z.string().optional(),
        type: phoneTypeEnum,
        primary: z.boolean(),
      }),
    ),
    socialProfiles: z.array(socialProfileSchema),
    addresses: z.array(addressSchema),
  }),
  tags: z.array(z.string()).optional(),
  status: z
    .object({
      active: z.boolean().optional(),
      doNotContact: z.boolean().optional(),
      verified: z.boolean().optional(),
      vip: z.boolean().optional(),
      reason: z.string().optional(),
    })
    .optional(),
  consents: z
    .object({
      marketing: z.boolean().optional(),
      analytics: z.boolean().optional(),
      thirdParty: z.boolean().optional(),
    })
    .optional(),
  czechFields: z
    .object({
      vokativ: z.string().optional(),
      vokativSurname: z.string().optional(),
      nameday: z.string().optional(),
    })
    .optional(),
  rating: z
    .object({
      score: z.number().min(0).max(100).optional(),
      engagement: z.enum(['', 'high', 'medium', 'low']).optional(),
    })
    .optional(),
  education: z.array(educationSchema),
  certifications: z.array(certificationSchema),
  skills: z.array(skillSchema),
  employment: z.array(employmentSchema),
  achievements: z.array(achievementSchema),
  references: z.array(referenceSchema),
  services: z.array(serviceSchema),
  eventsAttended: z.array(eventSchema),
  interactions: z.array(interactionSchema),
  notes: z.array(noteSchema),
  attachments: z.array(attachmentSchema),
  jobApplications: z.array(jobApplicationSchema),
});

export type ContactFormValues = z.infer<typeof contactFormSchema>;
export type EducationFormItem = z.infer<typeof educationSchema>;
export type CertificationFormItem = z.infer<typeof certificationSchema>;
export type SkillFormItem = z.infer<typeof skillSchema>;
export type EmploymentFormItem = z.infer<typeof employmentSchema>;
export type AchievementFormItem = z.infer<typeof achievementSchema>;
export type ReferenceFormItem = z.infer<typeof referenceSchema>;
export type ServiceFormItem = z.infer<typeof serviceSchema>;
export type EventFormItem = z.infer<typeof eventSchema>;
export type InteractionFormItem = z.infer<typeof interactionSchema>;
export type NoteFormItem = z.infer<typeof noteSchema>;
export type PhoneFormItem = z.infer<typeof phoneSchema>;
export type EmailFormItem = z.infer<typeof emailEntrySchema>;
export type SocialProfileFormItem = z.infer<typeof socialProfileSchema>;
export type AddressFormItem = z.infer<typeof addressSchema>;
export type AttachmentFormItem = z.infer<typeof attachmentSchema>;
export type JobApplicationFormItem = z.infer<typeof jobApplicationSchema>;

// ------------------------------------------------------------------ defaults

export const defaultFormValues: ContactFormValues = {
  profile: {
    titleBefore: '',
    firstName: '',
    middleName: '',
    lastName: '',
    titleAfter: '',
    dateOfBirth: undefined,
    gender: '',
    residence: '',
    city: '',
    nationality: '',
    languages: [],
    photoUrl: '',
    bio: '',
    interest: '',
    region: '',
    country: '',
    educationInstitution: '',
    occupation: '',
    specialization: '',
    company: '',
    position: '',
  },
  contact: {
    primaryEmail: '',
    emails: [],
    phones: [],
    socialProfiles: [],
    addresses: [],
  },
  tags: [],
  status: { active: true, doNotContact: false, verified: false, vip: false, reason: '' },
  consents: { marketing: false, analytics: false, thirdParty: false },
  czechFields: { vokativ: '', vokativSurname: '', nameday: '' },
  rating: { score: undefined, engagement: '' },
  education: [],
  certifications: [],
  skills: [],
  employment: [],
  achievements: [],
  references: [],
  services: [],
  eventsAttended: [],
  interactions: [],
  notes: [],
  attachments: [],
  jobApplications: [],
};

// ------------------------------------------------------------------ Contact → form

export const contactToFormValues = (c: Contact): ContactFormValues => ({
  profile: {
    titleBefore: c.profile.titleBefore ?? '',
    firstName: c.profile.firstName,
    middleName: c.profile.middleName ?? '',
    lastName: c.profile.lastName,
    titleAfter: c.profile.titleAfter ?? '',
    dateOfBirth: c.profile.dateOfBirth ? new Date(c.profile.dateOfBirth) : undefined,
    gender: parseGender(c.profile.gender),
    residence: c.profile.residence ?? '',
    city: c.profile.city ?? '',
    nationality: c.profile.nationality ?? '',
    languages: c.profile.languages ?? [],
    photoUrl: c.profile.photoUrl ?? '',
    bio: c.profile.bio ?? '',
    interest: c.profile.interest ?? '',
    region: c.profile.region ?? '',
    country: c.profile.country ?? '',
    educationInstitution: c.profile.educationInstitution ?? '',
    occupation: c.profile.occupation ?? '',
    specialization: c.profile.specialization ?? '',
    company: c.profile.company ?? '',
    position: c.profile.position ?? '',
  },
  contact: {
    primaryEmail: c.contact.primaryEmail,
    emails:
      c.contact?.emails?.map((e) => ({
        email: e.email,
        type: parseEmailType(e.type),
      })) ?? [],
    phones:
      c.contact?.phones?.map((p) => ({
        number: p.number ?? '',
        type: parsePhoneType(p.type),
        primary: p.primary ?? false,
      })) ?? [],
    socialProfiles:
      c.contact?.socialProfiles?.map((s) => ({
        platform: safeEnum(socialPlatformEnum, s.platform),
        url: s.url ?? '',
        username: s.username ?? '',
      })) ?? [],
    addresses:
      c.contact?.addresses?.map((a) => ({
        type: safeEnum(addressTypeEnum, a.type),
        street: a.street ?? '',
        city: a.city ?? '',
        state: a.state ?? '',
        postalCode: a.postalCode ?? '',
        country: a.country ?? '',
        primary: a.primary ?? false,
      })) ?? [],
  },
  tags: c.tags ?? [],
  status: {
    active: c.status?.active ?? true,
    doNotContact: c.status?.doNotContact ?? false,
    verified: c.status?.verified ?? false,
    vip: c.status?.vip ?? false,
    reason: c.status?.reason ?? '',
  },
  consents: {
    marketing: c.consents?.marketing?.value ?? false,
    analytics: c.consents?.analytics?.value ?? false,
    thirdParty: c.consents?.thirdParty?.value ?? false,
  },
  czechFields: {
    vokativ: c.czechFields?.vokativ ?? '',
    vokativSurname: c.czechFields?.vokativSurname ?? '',
    nameday: c.czechFields?.nameday ?? '',
  },
  rating: {
    score: c.rating?.score,
    engagement: parseEngagement(c.rating?.engagement),
  },
  education: (c.education ?? []).map((e) => ({
    institution: e.institution,
    faculty: e.faculty ?? '',
    field: e.field ?? '',
    degree: e.degree ?? '',
    status: safeEnum(educationStatusEnum, e.status),
    yearOfStudy: e.yearOfStudy,
    startDate: isoToDate(e.startDate),
    endDate: isoToDate(e.endDate),
    location: e.location ?? '',
  })),
  certifications: (c.certifications ?? []).map((ce) => ({
    name: ce.name,
    issuer: ce.issuer ?? '',
    credentialId: ce.credentialId ?? '',
    issuedOn: isoToDate(ce.issuedOn),
    expiresOn: isoToDate(ce.expiresOn),
    url: ce.url ?? '',
  })),
  skills: (c.skills ?? []).map((s) => ({
    name: s.name,
    category: safeEnum(skillCategoryEnum, s.category),
    level: safeEnum(skillLevelEnum, s.level),
  })),
  employment: (c.employment ?? []).map((e) => ({
    organization: e.organization,
    role: e.role,
    department: e.department ?? '',
    employmentType: safeEnum(employmentTypeEnum, e.employmentType),
    startDate: isoToDate(e.startDate),
    endDate: isoToDate(e.endDate),
    location: e.location ?? '',
    description: e.description ?? '',
  })),
  achievements: (c.achievements ?? []).map((a) => ({
    type: a.type ?? '',
    title: a.title ?? '',
    description: a.description ?? '',
    date: isoToDate(a.date),
    url: a.url ?? '',
  })),
  references: (c.references ?? []).map((r) => ({
    name: r.name,
    relationship: safeEnum(referenceRelationshipEnum, r.relationship),
    position: r.position ?? '',
    organization: r.organization ?? '',
    email: r.email ?? '',
    phone: r.phone ?? '',
  })),
  services: (c.services ?? []).map((s) => ({
    serviceName: s.serviceName ?? '',
    type: safeEnum(serviceTypeEnum, s.type),
    status: safeEnum(serviceStatusEnum, s.status),
    purchasedOn: isoToDate(s.purchasedOn),
    expiresOn: isoToDate(s.expiresOn),
  })),
  eventsAttended: (c.eventsAttended ?? []).map((e) => ({
    eventName: e.eventName ?? '',
    type: safeEnum(eventTypeEnum, e.type),
    role: safeEnum(eventRoleEnum, e.role),
    startDate: isoToDate(e.startDate),
    endDate: isoToDate(e.endDate),
    location: e.location ?? '',
  })),
  interactions: (c.interactions ?? []).map((i) => ({
    type: safeEnum(interactionTypeEnum, i.type),
    subject: i.subject ?? '',
    summary: i.summary ?? '',
    occurredAt: isoToDate(i.occurredAt),
    outcome: i.outcome ?? '',
  })),
  notes: (c.notes ?? []).map((n) => ({
    text: n.text ?? '',
    category: safeEnum(noteCategoryEnum, n.category),
    addedAt: n.addedAt ?? '',
    addedBy: n.addedBy ?? '',
  })),
  attachments: (c.attachments ?? []).map((a) => ({
    fileId: a.fileId,
    name: a.name,
    category: a.category ?? '',
    type: a.type ?? '',
    size: a.size,
    url: a.url ?? '',
    uploadedAt: a.uploadedAt ?? '',
    uploadedBy: a.uploadedBy ?? '',
  })),
  jobApplications: (c.jobApplications ?? []).map((j) => ({
    date: isoToDate(j.date),
    portal: j.portal ?? '',
    jobId: j.jobId ?? '',
    jobTitle: j.jobTitle ?? '',
    jobText: j.jobText ?? '',
    institutionName: j.institutionName ?? '',
    message: j.message ?? '',
    attachments: (j.attachments ?? []).map((a) => ({
      fileId: a.fileId,
      name: a.name,
      category: a.category ?? '',
      type: a.type ?? '',
      size: a.size,
      url: a.url ?? '',
      uploadedAt: a.uploadedAt ?? '',
      uploadedBy: a.uploadedBy ?? '',
    })),
  })),
});

// ------------------------------------------------------------------ form → Contact

export const formValuesToContact = (v: ContactFormValues, prev?: Contact): ApiContact => {
  const now = new Date().toISOString();
  const buildConsent = (key: 'marketing' | 'analytics' | 'thirdParty') => {
    const nextValue = v.consents?.[key];
    const previous = prev?.consents?.[key];
    if (nextValue === undefined) return previous;
    if (!previous && !nextValue) return undefined;
    if (previous && previous.value === nextValue) return previous;
    return {
      ...(previous ?? {}),
      value: nextValue,
      method: previous?.method ?? 'web',
      updatedAt: now,
    };
  };

  // Server owns audit (createdAt/updatedAt/version) — supply prev's value when
  // updating, otherwise a placeholder that the API replaces on create.
  const audit = prev?.audit ?? {
    createdAt: now,
    createdBy: '',
    updatedAt: now,
    updatedBy: '',
    version: 1,
  };

  return {
    ...v,
    audit,
    profile: {
      ...v.profile,
      firstName: v.profile.firstName ?? '',
      lastName: v.profile.lastName ?? '',
      dateOfBirth: v.profile.dateOfBirth ? formatISO(v.profile.dateOfBirth) : undefined,
    },
    contact: {
      ...v.contact,
      socialProfiles: v.contact.socialProfiles.length
        ? v.contact.socialProfiles
            .filter((s) => s.url || s.username || s.platform)
            .map((s) => ({
              platform: s.platform || undefined,
              url: s.url || undefined,
              username: s.username || undefined,
            }))
        : undefined,
      addresses: v.contact.addresses.length
        ? v.contact.addresses
            .filter((a) => a.street || a.city || a.postalCode || a.country)
            .map((a) => ({
              ...a,
              type: a.type || undefined,
            }))
        : undefined,
    },
    tags: v.tags && v.tags.length > 0 ? v.tags : undefined,
    status: v.status,
    czechFields: v.czechFields,
    rating:
      v.rating && (v.rating.score !== undefined || v.rating.engagement)
        ? {
            ...prev?.rating,
            score: v.rating.score,
            engagement: v.rating.engagement || undefined,
          }
        : prev?.rating,
    consents: {
      marketing: buildConsent('marketing'),
      analytics: buildConsent('analytics'),
      thirdParty: buildConsent('thirdParty'),
    },
    education: v.education.length
      ? v.education.map((e) => ({
          ...e,
          status: e.status || undefined,
          startDate: dateToIso(e.startDate),
          endDate: dateToIso(e.endDate),
        }))
      : undefined,
    certifications: v.certifications.length
      ? v.certifications.map((ce) => ({
          ...ce,
          issuedOn: dateToIso(ce.issuedOn),
          expiresOn: dateToIso(ce.expiresOn),
        }))
      : undefined,
    skills: v.skills.length
      ? v.skills.map((s) => ({
          ...s,
          category: s.category || undefined,
          level: s.level || undefined,
        }))
      : undefined,
    employment: v.employment.length
      ? v.employment.map((e) => ({
          ...e,
          employmentType: e.employmentType || undefined,
          startDate: dateToIso(e.startDate),
          endDate: dateToIso(e.endDate),
        }))
      : undefined,
    achievements: v.achievements.length
      ? v.achievements.map((a) => ({
          ...a,
          date: dateToIso(a.date),
        }))
      : undefined,
    references: v.references.length
      ? v.references.map((r) => ({
          ...r,
          relationship: r.relationship || undefined,
          email: r.email || undefined,
        }))
      : undefined,
    services: v.services.length
      ? v.services.map((s) => ({
          ...s,
          type: s.type || undefined,
          status: s.status || undefined,
          purchasedOn: dateToIso(s.purchasedOn),
          expiresOn: dateToIso(s.expiresOn),
        }))
      : undefined,
    eventsAttended: v.eventsAttended.length
      ? v.eventsAttended.map((e) => ({
          ...e,
          type: e.type || undefined,
          role: e.role || undefined,
          startDate: dateToIso(e.startDate),
          endDate: dateToIso(e.endDate),
        }))
      : undefined,
    interactions: v.interactions.length
      ? v.interactions.map((i) => ({
          ...i,
          type: i.type || undefined,
          occurredAt: dateToIso(i.occurredAt),
        }))
      : undefined,
    notes: v.notes.length
      ? v.notes.map((n) => ({
          ...n,
          category: n.category || undefined,
        }))
      : undefined,
    attachments: v.attachments.length
      ? v.attachments.map((a) => ({
          ...a,
          category: a.category || undefined,
          type: a.type || undefined,
          url: a.url || undefined,
        }))
      : undefined,
    jobApplications: v.jobApplications.length
      ? v.jobApplications.map((j) => ({
          ...j,
          date: dateToIso(j.date),
          portal: j.portal || undefined,
          jobId: j.jobId || undefined,
          jobTitle: j.jobTitle || undefined,
          jobText: j.jobText || undefined,
          institutionName: j.institutionName || undefined,
          message: j.message || undefined,
          attachments: j.attachments.length
            ? j.attachments.map((a) => ({
                ...a,
                category: a.category || undefined,
                type: a.type || undefined,
                url: a.url || undefined,
              }))
            : undefined,
        }))
      : undefined,
  };
};

// export const formValuesToContact = (v: ContactFormValues, updatedBy: string): Partial<Contact> => {
//   const now = new Date().toISOString();
//   return {
//     profile: {
//       titleBefore: str(v.profile.titleBefore),
//       firstName: v.profile.firstName,
//       middleName: str(v.profile.middleName),
//       lastName: v.profile.lastName,
//       titleAfter: str(v.profile.titleAfter),
//       dateOfBirth: v.profile.dateOfBirth instanceof Date
//         ? v.profile.dateOfBirth.toISOString().slice(0, 10)
//         : str(v.profile.dateOfBirth),
//       gender: str(v.profile.gender),
//       residence: str(v.profile.residence),
//       nationality: str(v.profile.nationality),
//       languages: v.profile.languages
//         ? v.profile.languages
//             .split(',')
//             .map((s) => s.trim())
//             .filter(Boolean)
//             .map((name) => ({ code: name.toLowerCase().substring(0, 2), name }))
//         : [],
//       photoUrl: str(v.profile.photoUrl),
//       bio: str(v.profile.bio),
//     },
//     contact: {
//       primaryEmail: v.contact.primaryEmail,
//       emails: v.contact.emails,
//       phones: v.contact.phones,
//       socialProfiles: v.contact.socialProfiles,
//     },
//     tags: v.tags
//       ? v.tags
//           .split(',')
//           .map((t) => t.trim())
//           .filter(Boolean)
//       : [],
//     status: v.status,
//     consents: {
//       marketing: { value: v.consents.marketing, method: 'web', updatedAt: now, updatedBy },
//       analytics: { value: v.consents.analytics, method: 'web', updatedAt: now, updatedBy },
//       thirdParty: { value: v.consents.thirdParty, method: 'web', updatedAt: now, updatedBy },
//     },
//     education: v.education.map((e) => ({
//       ...e,
//       collectedAt: now,
//     })),
//     certifications: v.certifications.map((c) => ({ ...c, collectedAt: now })),
//     skills: v.skills.map((s) => ({ ...s, collectedAt: now })),
//     employment: v.employment.map((e) => ({ ...e, collectedAt: now })),
//     achievements: v.achievements.map((a) => ({ ...a, collectedAt: now })),
//     references: v.references,
//     services: v.services.map((s) => ({ ...s, collectedAt: now })),
//     eventsAttended: v.eventsAttended.map((e) => ({ ...e, collectedAt: now })),
//     interactions: v.interactions,
//     notes: v.notes,
//     czechFields: {
//       vokativ: str(v.czechFields.vokativ),
//       vokativSurname: str(v.czechFields.vokativSurname),
//       nameday: str(v.czechFields.nameday),
//     },
//     rating:
//       v.rating.score !== undefined || v.rating.engagement
//         ? {
//             score: v.rating.score,
//             engagement: (v.rating.engagement as 'high' | 'medium' | 'low' | undefined) || undefined,
//           }
//         : undefined,
//   };
// };
